import React from 'react';
import { Drawer, Spin, Table, Input } from 'antd';
import { Store, Search, Inbox } from 'lucide-react';
import { EmptyState, StatusTag } from '../common';
import { monthHeader } from '../uso-de-shelf/usageHeat';
import { titleize } from '../share-of-shelf/sosHeat';
import StoreDnDrawerController from './storeDnDrawer.controller';
import { PresenceCell, PresenceLegend } from './elements/Presence';

/**
 * Drawer showing a store's FULL Distribución Numérica per product: rows = every product seen in the
 * store's checks, columns = months (presence), plus the current (latest) status.
 */
export default class StoreDnDrawer extends StoreDnDrawerController {
    state = { loading: false, loadedId: null, data: null, q: '' };

    get columns() {
        const months = this.state.data?.months || [];
        // Presence badge for one product-month: ✓ present / ✗ seen-but-absent / — not seen that month.
        const monthCols = months.map((m) => ({
            title: monthHeader(m),
            key: m,
            align: 'center',
            width: 78,
            onCell: () => ({ style: { padding: 0 } }),
            render: (_, r) => <PresenceCell audited={!!r.seen?.[m]} present={!!r.byMonth?.[m]} />
        }));
        return [
            {
                title: 'Producto',
                dataIndex: 'name',
                key: 'name',
                fixed: 'left',
                width: 220,
                render: (t, r) => (
                    <div>
                        <div className="font-medium text-surface-900">{t}</div>
                        {r.category ? <span className="text-xs text-surface-400">{titleize(r.category)}</span> : null}
                    </div>
                )
            },
            ...monthCols,
            {
                title: 'Actual',
                key: '__current',
                align: 'center',
                width: 104,
                fixed: 'right',
                render: (_, r) =>
                    r.current ? (
                        <StatusTag tone="success" dot>
                            Presente
                        </StatusTag>
                    ) : (
                        <StatusTag tone="danger" dot>
                            Ausente
                        </StatusTag>
                    )
            }
        ];
    }

    render() {
        const { open, onClose, storeName, clientName } = this.props;
        const { loading, data, q } = this.state;
        const { all, rows, presentNow } = this.productRows();

        return (
            <Drawer
                open={open}
                onClose={onClose}
                width={720}
                title={
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-info-bg text-info-fg">
                            <Store />
                        </span>
                        <div className="min-w-0">
                            <div className="truncate font-semibold text-surface-900">{storeName || 'PDV'}</div>
                            {clientName ? <div className="truncate text-xs font-normal text-surface-500">{clientName}</div> : null}
                        </div>
                    </div>
                }>
                {loading ? (
                    <div className="flex justify-center py-16">
                        <Spin />
                    </div>
                ) : !data || all.length === 0 ? (
                    <EmptyState icon={<Inbox />} title="Sin datos" description="Sin productos registrados para este PDV" />
                ) : (
                    <>
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <span className="text-sm text-surface-600 tabular-nums">
                                DN por producto · <span className="font-semibold text-surface-900">{presentNow}/{all.length}</span> presentes actualmente
                            </span>
                            <PresenceLegend missing={<>— sin registro ese mes</>} />
                        </div>
                        <Input
                            allowClear
                            prefix={<Search className="text-surface-400" />}
                            placeholder="Buscar producto o categoría"
                            value={q}
                            onChange={(e) => this.setQuery(e.target.value)}
                            className="mb-3"
                        />
                        <div className="overflow-hidden rounded-lg border border-surface-200">
                            <Table
                                className="heat-table"
                                columns={this.columns}
                                dataSource={rows}
                                rowKey={(r) => r.name}
                                size="small"
                                scroll={{ x: 'max-content' }}
                                pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} productos` }}
                            />
                        </div>
                    </>
                )}
            </Drawer>
        );
    }
}
